/* eslint-disable max-statements */
import React, { useEffect } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './style.css';

type Props = {
	totalCount: number;
	totalPages: number;
	itemsPerPage: number;
	currentPage: number;
	setPage: any;
};

const SHOWN_PAGES = 7;

const Pagination = (props: Props) => {
	const { totalPages, currentPage, setPage } = props;

	useEffect(() => {
		setPage(currentPage);
	}, [currentPage]);

	const onNextPage = (): void => {
		setPage(currentPage + 1);
		console.log(`Next Page: ${currentPage}`);
	};
	const onPreviousPage = (): void => {
		setPage(currentPage - 1);
		console.log(`Previous Page: ${currentPage}`);
	};

	type pageNumberType = {
		page: number;
		isCurrent?: boolean;
	};

	type loopPagesType = {
		start?: number;
		limit?: number;
	};

	const PageNumber = ({ page, isCurrent = false }: pageNumberType) => {
		return isCurrent ? (
			<a
				href="#"
				aria-current="page"
				className="z-10 dark:bg-gray-600 bg-indigo-50 border-purple-600 text-purple-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all duration-500"
			>
				{page}
			</a>
		) : (
			<a
				href="#"
				className="bg-white dark:bg-gray-600 dark:text-white dark:border-gray-700 border-gray-300 text-gray-500 hover:bg-gray-300 hidden md:inline-flex relative items-center px-4 py-2 border text-sm font-medium transition-all duration-500"
				onClick={() => {
					setPage(page);
				}}
			>
				{page}
			</a>
		);
	};

	const LoopPages = ({ start = 1, limit = totalPages }: loopPagesType) => {
		const steps = [];
		for (let i = start; i <= limit; i++) steps.push(<PageNumber page={i} />);
		return <>{steps}</>;
	};

	type PreviousOrNextPageType = {
		disabled?: boolean;
	};

	const PreviousPage = ({ disabled = false }: PreviousOrNextPageType) => {
		return disabled ? (
			<a
				href="#"
				className="relative inline-flex items-center px-2 py-2 rounded-l-md border
				 border-gray-400 bg-gray-400 text-sm font-medium 
				 text-gray-500 
				 dark:bg-gray-700 dark:text-white dark:border-gray-700 transition-all duration-500"
			>
				<span className="sr-only">Previous</span>
				<FaChevronLeft size={13} className="w-5" aria-hidden="true" />
			</a>
		) : (
			<a
				href="#"
				className="relative inline-flex items-center px-2 py-2 rounded-l-md border 
				border-gray-300 bg-white text-gray-500 hover:bg-gray-300
				dark:bg-gray-600 dark:text-white dark:border-gray-700 transition-all duration-500"
				onClick={() => onPreviousPage()}
			>
				<FaChevronLeft size={13} className="w-5" aria-hidden="true" />
			</a>
		);
	};

	const NextPage = ({ disabled }: PreviousOrNextPageType) => {
		return disabled ? (
			<a
				href="#"
				className="relative inline-flex items-center px-2 py-2 rounded-r-md border 
				border-gray-400 bg-gray-400 text-sm font-medium text-gray-500
				dark:bg-gray-700 dark:text-white dark:border-gray-700 transition-all duration-500"
			>
				<span className="sr-only">Next</span>
				<FaChevronRight size={13} className="w-5" aria-hidden="true" />
			</a>
		) : (
			<a
				href="#"
				className="relative inline-flex items-center px-2 py-2 rounded-r-md border 
				border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-300
				dark:bg-gray-600 dark:text-white dark:border-gray-700 transition-all duration-500"
				onClick={() => onNextPage()}
			>
				<span className="sr-only">Next</span>
				<FaChevronRight size={13} className="w-5" aria-hidden="true" />
			</a>
		);
	};

	const GetPreviousPagination = () => {
		if (currentPage <= 1) {
			return (
				<>
					<PreviousPage disabled={true} />
					<PageNumber page={currentPage} isCurrent={true} />

					<LoopPages start={currentPage + 1} limit={SHOWN_PAGES} />
				</>
			);
		} else {
			if (currentPage > totalPages - SHOWN_PAGES + 2) {
				return (
					<>
						<PreviousPage />

						<PageNumber page={1} />
						<Dots />
						<LoopPages
							start={totalPages - SHOWN_PAGES + 1}
							limit={currentPage - 1}
						/>
						<PageNumber page={currentPage} isCurrent={true} />
					</>
				);
			}
			if (currentPage > SHOWN_PAGES - 2) {
				return (
					<>
						<PreviousPage />

						<PageNumber page={1} />
						<Dots />
						<PageNumber page={currentPage - 2} />
						<PageNumber page={currentPage - 1} />
						<PageNumber page={currentPage} isCurrent={true} />
					</>
				);
			}
			return (
				<>
					<PreviousPage />
					<LoopPages start={1} limit={currentPage - 1} />
					<PageNumber page={currentPage} isCurrent={true} />
					<LoopPages start={currentPage + 1} limit={SHOWN_PAGES} />
				</>
			);
		}
	};

	const GetNextPagination = () => {
		if (currentPage >= totalPages) {
			return (
				<>
					<NextPage disabled={true} />
				</>
			);
		} else {
			if (currentPage < SHOWN_PAGES - 1) {
				return (
					<>
						<Dots />
						<PageNumber page={totalPages} />
						<NextPage />
					</>
				);
			}

			if (currentPage < totalPages - (SHOWN_PAGES - 3)) {
				return (
					<>
						<PageNumber page={currentPage + 1} />
						<PageNumber page={currentPage + 2} />
						<Dots />
						<PageNumber page={totalPages} />
						<NextPage />
					</>
				);
			}
			return (
				<>
					<LoopPages start={currentPage + 1} limit={totalPages} />
					<NextPage />
				</>
			);
		}
	};

	const GetAllPages = () => {
		return (
			<>
				{currentPage > 1 ? <PreviousPage /> : <PreviousPage disabled={true} />}
				<LoopPages start={1} limit={currentPage - 1} />
				<PageNumber page={currentPage} isCurrent={true} />
				<LoopPages start={currentPage + 1} limit={totalPages} />
				{currentPage < totalPages ? <NextPage /> : <NextPage disabled={true} />}
			</>
		);
	};

	return (
		<nav
			className="mt-2 relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
			aria-label="Pagination"
		>
			{totalPages > 10 ? (
				<>
					<GetPreviousPagination />
					<GetNextPagination />
				</>
			) : (
				<GetAllPages />
			)}
		</nav>
	);
};

const Dots = () => {
	return (
		<span className="bg-white dark:bg-gray-600 dark:text-white dark:border-gray-700 border-gray-300 text-gray-500 hover:bg-gray-300 hidden md:inline-flex relative items-center px-4 py-2 border text-sm font-medium transition-all duration-500">
			...
		</span>
	);
};

export default Pagination;
